import React from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
}>;
const FiEyeOffComponent = FiEyeOff as unknown as React.ComponentType<{
  size?: number | string;
}>;

const PasswordModal: React.FC<{
  onSubmit: (values: { password: string; confirmPassword: string }) => void;
  onClose: () => void;
  isForgotPassword?: boolean;
}> = ({ onSubmit, onClose, isForgotPassword = false }) => {
  // Password validation schema
  const passwordSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, '8 character minimum')
      .matches(/[a-z]/, 'One lowercase character')
      .matches(/[A-Z]/, 'One uppercase character')
      .matches(/[\d\W]/, 'One number or special symbol')
      .required('Required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Required'),
  });

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'>
      <div
        className='bg-white rounded-lg shadow-lg max-w-sm w-full'
        style={{
          padding: '20px',
        }}
      >
        <h3
          className='text-lg font-semibold mb-1'
          style={{
            color: '#3F5265',
            fontSize: '16px',
          }}
        >
          {isForgotPassword ? 'Set a new password' : 'Set a password'}
        </h3>
        <p
          className='text-sm text-gray-600 mb-6'
          style={{
            color: '#657584',
            fontSize: '12px',
          }}
        >
          {isForgotPassword ? (
            <>Choose a new password you haven't used before.</>
          ) : (
            <>
              Choose a password with at least one lowercase, one uppercase, one special symbol or digit &amp; 8 character minimum.
            </>
          )}
        </p>

        <Formik
          initialValues={{
            password: '',
            confirmPassword: '',
            showPassword: false,
            showConfirm: false,
          }}
          validationSchema={passwordSchema}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue, isValid }) => {
            const hasLowercase = /[a-z]/.test(values.password);
            const hasUppercase = /[A-Z]/.test(values.password);
            const hasSpecialOrDigit = /[\d\W]/.test(values.password);
            const minLength = values.password.length >= 8;
            const passwordsMatch =
              values.password === values.confirmPassword &&
              values.password !== '';

            return (
              <Form className='space-y-4'>
                <div
                  style={{
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    paddingBottom: '0px',
                  }}
                >
                  <div>
                    <label
                      className='block text-sm'
                      style={{ color: '#9FA9B2', fontSize: '10px' }}
                    >
                      {isForgotPassword ? 'NEW PASSWORD' : 'PASSWORD'}
                      <span className='text-red-500'>*</span>
                    </label>
                    <div className='relative'>
                      <Field
                        name='password'
                        type={values.showPassword ? 'text' : 'password'}
                        placeholder={
                          isForgotPassword
                            ? 'Set new password'
                            : 'Enter password'
                        }
                        className='w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400'
                      />
                      <button
                        type='button'
                        onClick={() =>
                          setFieldValue('showPassword', !values.showPassword)
                        }
                        className='absolute inset-y-0 right-3 flex items-center text-black-400'
                      >
                        {values.showPassword ? (
                          <FiEyeComponent size={18} />
                        ) : (
                          <FiEyeOffComponent size={18} />
                        )}
                      </button>
                    </div>

                    <ul
                      className='text-sm mt-1'
                      style={{
                        lineHeight: 1.5,
                        paddingLeft: '5px',
                      }}
                    >
                      <li
                        style={{
                          color: hasLowercase ? 'green' : 'orange',
                          fontSize: '10px',
                        }}
                      >
                        • One lowercase character
                      </li>
                      <li
                        style={{
                          color: hasUppercase ? 'green' : 'orange',
                          fontSize: '10px',
                        }}
                      >
                        • One uppercase character
                      </li>
                      <li
                        style={{
                          color: hasSpecialOrDigit ? 'green' : 'orange',
                          fontSize: '10px',
                        }}
                      >
                        • One number or special symbol
                      </li>
                      <li
                        style={{
                          color: minLength ? 'green' : 'orange',
                          fontSize: '10px',
                        }}
                      >
                        • 8 character minimum
                      </li>
                    </ul>
                  </div>

                  <div>
                    <label
                      className='block text-sm mt-3'
                      style={{ color: '#9FA9B2', fontSize: '10px' }}
                    >
                      {isForgotPassword
                        ? 'CONFIRM NEW PASSWORD'
                        : 'CONFIRM PASSWORD'}
                      <span className='text-red-500'>*</span>
                    </label>
                    <div className='relative'>
                      <Field
                        name='confirmPassword'
                        type={values.showConfirm ? 'text' : 'password'}
                        placeholder={
                          isForgotPassword
                            ? 'Re-enter new password'
                            : 'Re-enter password'
                        }
                        className='w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400'
                      />
                      <button
                        type='button'
                        onClick={() =>
                          setFieldValue('showConfirm', !values.showConfirm)
                        }
                        className='absolute inset-y-0 right-3 flex items-center text-black-400'
                      >
                        {values.showConfirm ? (
                          <FiEyeComponent size={18} />
                        ) : (
                          <FiEyeOffComponent size={18} />
                        )}
                      </button>
                    </div>

                    {values.confirmPassword && (
                      <p
                        className='mt-1'
                        style={{
                          color: passwordsMatch ? 'green' : 'red',
                          fontSize: '10px',
                          paddingLeft: '10px',
                        }}
                      >
                        {passwordsMatch
                          ? 'Password Matched'
                          : 'Passwords do not match'}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type='submit'
                  disabled={!isValid || !passwordsMatch}
                  className={`px-3 py-1 rounded-sm text-sm font-medium text-white ${isValid && passwordsMatch
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : 'bg-gray-300 cursor-not-allowed'
                    }`}
                  style={{
                    fontSize: '12px',
                    float: 'right',
                  }}
                >
                  Continue
                </button>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default PasswordModal;